/**
 * GAME-SHAPED FORM CONTROLS.
 *
 * The settings screen rendered native radios, checkboxes and a `<select>`. They
 * work, they are accessible, and they look like a web form dropped into a card
 * game — which is what the owner said on landing there: it reads as "the
 * website", not the game.
 *
 * ── THE PATTERN, AND WHY NOT BUTTONS ────────────────────────────────────────
 * The obvious move is `role="radio"` buttons with roving tabindex. I built that
 * first and threw it away: this is the ACCESSIBILITY panel, and its tests drive
 * it the way assistive technology does — `input.checked = true`, a `change`
 * event, `getByRole('checkbox')`. Replacing the inputs would have meant
 * rewriting accessibility tests to make the screen prettier, which is a bad
 * trade and the kind of change that quietly removes real support.
 *
 * So the input STAYS and does all the work — focus, keyboard, arrow-key group
 * semantics, screen-reader announcement, form state — and is visually hidden
 * (not `display:none`, which would take it out of the accessibility tree). The
 * visible control is the label, styled with `:has()` off the input's own
 * `:checked` and `:focus-visible` state. Nothing to keep in sync, because there
 * is only one source of truth.
 *
 * Depth is the point: a raised plate for the chosen option and a travelling
 * knob for the switch, so both read as physical objects rather than as
 * coloured rectangles (docs/UI-STYLE.md).
 *
 * ── THE MICRO-INTERACTION VOCABULARY, AND ITS ONE HARD RULE ─────────────────
 * Every control in this sheet says the same five things the same way:
 *
 *   hover     rise 1px, deepen the lift    (decoration — dropped under
 *                                           prefers-reduced-motion and on touch)
 *   press     sink 3px, collapse the lift  (feedback — kept under reduced
 *                                           motion, just instant)
 *   release   the same 140ms spring back
 *   focus     a 2px gold ring, and NO movement
 *   disabled  no movement at all, flattened lift, .55 opacity, not-allowed
 *
 * Only `transform` and `opacity` move; nothing here animates a width, a height
 * or an offset, because those dirty layout on every frame of every press.
 *
 * THE HARD RULE: **no state rule in this file may declare `transform`.** Every
 * base rule composes one transform out of custom properties and every state
 * writes a variable instead. The reason is `_CONTRACTS.md` §6.5 rule 2 — two
 * unlayered rules setting the same property on one element are decided by
 * SOURCE ORDER, invisibly, and this sheet has already lost that fight twice
 * (the plate vs. the press scale, and the switch knob's travel vs. its squash).
 * The full account is in the contract note at the top of `press.ts`'s
 * `PRESS_CSS`, which declares the same variables.
 */


export const CONTROLS_CSS = `
/* The input keeps every semantic; only its pixels go away. Not display:none —
   that would remove it from the accessibility tree along with the picture. */
.rga-opt__input{position:absolute;width:1px;height:1px;margin:-1px;padding:0;
  overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0}

/* ── segmented group ──────────────────────────────────────────────────── */
.rga-seg{display:flex;flex-direction:column;gap:.35rem;padding:.3rem;border-radius:12px;
  background:color-mix(in srgb,var(--color-surface-2) 70%,transparent);
  border:1px solid var(--color-border);box-shadow:var(--rga-depth)}
@media (min-width:30rem){.rga-seg{flex-direction:row}}
.rga-opt{--rga-press-y:0px;--rga-press-s:1;
  flex:1 1 0;display:flex;align-items:center;gap:.5rem;min-height:44px;
  padding:.5rem .7rem;border:1px solid transparent;border-radius:9px;
  color:var(--color-text-dim);cursor:pointer;
  transform:translate3d(0,var(--rga-press-y,0px),0) scale(var(--rga-press-s,1));
  transition:transform 140ms cubic-bezier(.34,1.56,.64,1),background 120ms linear,color 120ms linear}
.rga-opt:hover{color:var(--color-text)}
.rga-opt:active{--rga-press-s:.95}
@media (hover:hover) and (prefers-reduced-motion:no-preference){
.rga-opt:hover:not(:active){--rga-press-y:-1px}
}
/* The raised plate IS the checked state — never colour alone. */
.rga-opt:has(.rga-opt__input:checked){color:var(--color-text);background:var(--color-surface);
  border-color:var(--color-border);box-shadow:var(--rga-lift-1),var(--rga-sheen);font-weight:650}
/* THE FOCUS RING IS --color-gold, ON EVERY SURFACE THIS GAME PAINTS.
   On the dusk palette (owner rulings 2026-10-14) that is the warm gold #F2C14E:
   9.52:1 on the navy ground, 6.85:1 on a panel, 9.70:1 in a well and 5.94:1 on
   the header ribbon — over the 3:1 floor WCAG 2.2 SC 1.4.11 sets for a
   non-text cue.
   src/styles/tokens.test.ts holds these numbers.
   The 2px offset is the dark half of the ring: it shows the ground through and
   holds the gold off the plate's own --color-border rim, which is 2.38:1 from
   it and would otherwise merge into one fat smudge. Ring plus offset stay
   within 4px so the ring lands inside .rga-seg's .3rem padding rather than on
   the group's own border. */
.rga-opt:has(.rga-opt__input:focus-visible){outline:2px solid var(--color-gold);outline-offset:2px}
.rga-opt__dot{flex:0 0 auto;width:14px;height:14px;border-radius:999px;
  border:2px solid var(--color-border);background:var(--color-surface)}
.rga-opt:has(.rga-opt__input:checked) .rga-opt__dot{border-color:var(--color-gold);
  background:radial-gradient(circle at 50% 50%,var(--color-gold-bright) 0 45%,transparent 46%)}

/* ── switch ───────────────────────────────────────────────────────────── */
/* position:relative IS LOAD-BEARING. .rga-opt__input above is position:absolute,
   so it resolves against the nearest positioned ancestor. .rga-opt is one (its
   transform makes it a containing block); .rga-switch was NOT, and nothing
   between it and <html> was either, so the hidden checkbox escaped to the
   INITIAL containing block. There it sat at its static position far down the
   settings column, outside the shell's md:overflow-hidden, and stretched the
   DOCUMENT past one viewport. Clicking "Mute all sound" focused that checkbox,
   the browser scrolled the document to reveal it, and the whole 100dvh shell
   rode up — leaving a dead black band of <body> under it (owner report).
   The same box is the switch's own anchor, so the input never leaves it. */
.rga-switch{position:relative;display:flex;align-items:center;justify-content:space-between;gap:1rem;width:100%;
  min-height:44px;padding:.5rem .2rem;cursor:pointer}
.rga-switch__text{display:flex;flex-direction:column;gap:.1rem}
.rga-switch__label{font-weight:650;color:var(--color-text)}
.rga-switch__hint{font-size:.82em;color:var(--color-text-dim)}
.rga-switch__track{flex:0 0 auto;position:relative;width:54px;height:30px;border-radius:999px;
  background:var(--color-surface-2);border:1px solid var(--color-border);
  box-shadow:var(--rga-depth);transition:background 140ms ease}
.rga-switch:has(.rga-opt__input:checked) .rga-switch__track{background:var(--color-gold-bright);
  border-color:var(--color-gold)}
/* THE KNOB CARRIES TWO INDEPENDENT MOTIONS AND SO IT COMPOSES THEM, exactly as
   .rga-press/.rga-plate do above — the travel belongs to the checked state and
   the squash belongs to the press, and they overlap constantly (pressing a
   switch that is already on). Written as two transform declarations the
   checked rule and the active rule would fight, source order would decide, and
   pressing an ON switch would snap the knob back to the left for the duration
   of the press. That is _CONTRACTS.md §6.5 rule 2, on a second element.
   The squash used to be width:28px, which is a LAYOUT property: it dirtied
   layout on every press of every switch, and it grew the knob rightward out of
   its 54px track rather than about its own centre. */
.rga-switch__knob{--rga-knob-x:0px;--rga-knob-s:1;
  position:absolute;top:2px;left:2px;width:24px;height:24px;border-radius:999px;
  background:linear-gradient(180deg,#FFF8E8,#E6D9BC);box-shadow:0 2px 0 rgba(4,9,24,.5),0 2px 5px rgba(4,9,24,.4);
  transform:translateX(var(--rga-knob-x,0px)) scale(var(--rga-knob-s,1));
  transition:transform 140ms cubic-bezier(.34,1.4,.64,1)}
.rga-switch:has(.rga-opt__input:checked) .rga-switch__knob{--rga-knob-x:24px}
.rga-switch:has(.rga-opt__input:focus-visible){outline:2px solid var(--color-gold);
  outline-offset:2px;border-radius:10px}
.rga-switch:active .rga-switch__knob{--rga-knob-s:1.12}

/* ── select, given a plate and a drawn chevron ────────────────────────── */
.rga-select{position:relative;display:inline-flex;align-items:center;width:100%;max-width:22rem}
.rga-select select{appearance:none;width:100%;min-height:44px;padding:.55rem 2.4rem .55rem .8rem;
  border:1px solid var(--color-border);border-radius:10px;background:var(--color-surface);
  color:var(--color-text);font:inherit;font-weight:650;cursor:pointer;
  box-shadow:var(--rga-lift-1),var(--rga-sheen)}
.rga-select select:focus-visible{outline:2px solid var(--color-gold);outline-offset:2px}
.rga-select::after{content:'';position:absolute;right:.9rem;width:9px;height:9px;
  border-right:2.5px solid var(--color-gold);border-bottom:2.5px solid var(--color-gold);
  transform:translateY(-25%) rotate(45deg);pointer-events:none}

/* ── card, and the raised plate a whole button can wear ───────────────── */
.rga-card{border-radius:var(--rga-radius-panel,14px);border:var(--rga-rim,2px) solid var(--color-border);
  background:var(--rga-face,none),var(--color-surface);box-shadow:var(--rga-lift-1),var(--rga-sheen)}
/* ── WHY STATES ARE MODIFIER CLASSES HERE, NOT border-gold AT THE CALL SITE ──
   ensureUiStyles injects this sheet as a bare <style>, so its rules are
   UNLAYERED, while Tailwind v4 emits every utility inside @layer utilities.
   CSS Cascade 5 gives an unlayered normal declaration priority over a layered
   one regardless of specificity or source order, so .rga-plate's own
   border:1px solid var(--color-border) beats .border-gold on the very same
   element. A selected deck slot written as rga-plate border-gold painted no
   gold edge at all and differed from its neighbours by text colour alone. Any
   state a plate or card wears must be declared in THIS sheet or it does not
   exist on screen. Keep these names — the screens import nothing from here, so
   the class name IS the contract. */
/* The one panel on a screen that is asking to be acted on. Gold is the border
   and the lift, never the fill: a whole panel filled with --color-gold would
   put body text on a background it was never contrast-checked against, and the
   gold FILL is reserved for .rga-plate--primary, the single call to action.
   45% alpha was raised to 55%: this lift composites against the DARK ground,
   where a translucent gold loses more of itself than it did on the creme the
   value was first picked for. (No backticks in here: this file is one template
   literal.) */
.rga-card--gold{border-color:var(--color-gold);
  box-shadow:0 2px 0 color-mix(in srgb,var(--color-gold) 55%,transparent),var(--rga-sheen)}
/* A panel reporting something wrong with its own contents — a card the player
   does not own inside an imported deck. Full --color-error, never an alpha
   wash: #F2695E solid is 4.99:1 on --color-surface and 5.79:1 on the ground,
   over the 3:1 floor SC 1.4.11 sets for a boundary, and any translucent
   version of it drops under. The edge is still one channel; the panel has to
   say what is wrong in words as well, because colour alone is not a state
   (STYLE_BIBLE section 3). */
.rga-card--danger{border-color:var(--color-error);
  box-shadow:0 2px 0 color-mix(in srgb,var(--color-error) 55%,transparent),var(--rga-sheen)}
/* No width here on purpose: an injected stylesheet lands after Tailwind's, so
   a width:100% would silently beat every w-auto utility at the call site.
   Callers say how wide their own button is. */
/* ── THE COMPOSED TRANSFORM. Read press.ts's contract note before touching it ──
   --rga-press-y and --rga-press-s are declared and consumed by BOTH sheets.
   The plate owns the vertical travel, the press owns the scale, and because
   each writes a variable rather than transform itself they no longer beat one
   another: a plated button sinks 3px AND shrinks 5%. Written as two transform
   declarations — which is what shipped — CONTROLS_CSS is appended after
   PRESS_CSS in styles.ts, so the plate won on source order and the 95% press
   was silently dead on every button in the game (errata E2). Never put
   transform on a state rule in this file; put it on the base rule and let the
   state write a variable. test/ui-motion.test.ts resolves the cascade for
   real and fails if either half goes missing. */
.rga-plate{--rga-press-y:0px;--rga-press-s:1;
  display:flex;min-height:44px;border-radius:var(--rga-radius-control,12px);
  border:var(--rga-rim,2px) solid var(--color-border);background:var(--rga-face,none),var(--color-surface);
  box-shadow:var(--rga-lift-2),var(--rga-sheen);
  transform:translate3d(0,var(--rga-press-y,0px),0) scale(var(--rga-press-s,1));
  transition:transform 140ms cubic-bezier(.34,1.56,.64,1),box-shadow 140ms ease,opacity 140ms linear;
  cursor:pointer}
/* Hover deepens the lift; the 1px rise that goes with it is in the guarded
   block below, because a rise on pointer proximity is decoration. Touch never
   gets either: (hover:hover) keeps a latched :hover off a tapped button. */
.rga-plate:hover{box-shadow:var(--rga-lift-3),var(--rga-sheen)}
@media (hover:hover) and (prefers-reduced-motion:no-preference){
.rga-plate:hover:not(:active):not(.is-pressed):not([disabled]):not([aria-disabled=true]){--rga-press-y:-1px}
}
/* Press pushes the plate down ONTO its own shadow — the 3px travel is the
   whole reason the shadow is offset rather than blurred. */
.rga-plate:active{--rga-press-y:3px;box-shadow:0 0 0 var(--rga-lift-ink),var(--rga-sheen)}
/* Same ring as .rga-opt, for the same contrast reason — see the note there.
   Focus does not MOVE the plate: a ring that shifts its own target is hostile
   to the keyboard user tracking it. The ring is the entire cue. */
.rga-plate:focus-visible{outline:2px solid var(--color-gold);outline-offset:2px}
/* Disabled, in four channels and not one of them colour (STYLE_BIBLE §3): the
   attribute itself, the flattened lift (3px of travel gone, so it reads as
   already pressed into the page), the cursor, and no movement on any gesture.
   Both variables are pinned here and this is the LAST rule that writes them, so
   it takes the :hover and :active rules above at equal specificity. The
   --primary / --gold modifiers below still out-specify its box-shadow on
   :active, which is harmless: they restate a lift, never a transform, and a
   disabled <button> never goes :active in the first place. Body ink at .55 over
   --color-surface composites to #8D949C, 4.91:1, so a disabled label stays
   readable rather than becoming a rumour. */
.rga-plate[disabled],.rga-plate[aria-disabled=true]{opacity:.55;cursor:not-allowed;
  box-shadow:0 1px 0 var(--rga-lift-ink),var(--rga-sheen);
  --rga-press-y:0px;--rga-press-s:1}
.rga-plate--primary{background:linear-gradient(180deg,var(--color-gold-bright),color-mix(in srgb,var(--color-gold-bright) 78%,var(--color-gold)));
  border-color:var(--color-gold);color:var(--color-ink);font-weight:800;
  box-shadow:0 3px 0 color-mix(in srgb,var(--color-gold) 62%,#000),inset 0 1px 0 rgba(255,255,255,.34)}
.rga-plate--primary:hover{box-shadow:0 5px 0 color-mix(in srgb,var(--color-gold) 62%,#000),inset 0 1px 0 rgba(255,255,255,.34)}
.rga-plate--primary:active{box-shadow:0 0 0 color-mix(in srgb,var(--color-gold) 62%,#000),inset 0 1px 0 rgba(255,255,255,.34)}
/* The selected/active plate: a deck-slot tab, the live collection filter, an
   in-deck tile. Gold edge and gold lift, surface untouched — --primary is the
   ONE call to action on a screen and stays the only thing wearing the gold
   fill, so "chosen among peers" and "press this" never look alike.
   The :hover and :active shadows are restated so the lift stays GOLD through
   both, not to rescue the press: .rga-plate--gold is one class (0,1,0) and
   .rga-plate:active is class + pseudo-class (0,2,0), so the base rule already
   wins on press and the 3px travel was never at risk. Without the restatement
   a selected plate would simply drop back to the grey shadow the moment it was
   hovered, which reads as the selection being lost. */
.rga-plate--gold{border-color:var(--color-gold);
  box-shadow:0 3px 0 color-mix(in srgb,var(--color-gold) 55%,transparent),var(--rga-sheen)}
.rga-plate--gold:hover{box-shadow:0 5px 0 color-mix(in srgb,var(--color-gold) 55%,transparent),var(--rga-sheen)}
.rga-plate--gold:active{box-shadow:0 0 0 color-mix(in srgb,var(--color-gold) 55%,transparent),var(--rga-sheen)}
.rga-plate--danger{color:var(--color-error);border-color:color-mix(in srgb,var(--color-error) 35%,var(--color-border))}

/* ── REDUCED MOTION ───────────────────────────────────────────────────────
   What goes: the hover rise (its whole media block is gated on
   no-preference) and every spring — the travel and the squash land in one
   step instead of easing.
   What STAYS: the press cue itself. This block used to say transform:none,
   which left a reduced-motion player pressing plates that never acknowledged
   the press. Feedback on activation is not the non-essential animation WCAG
   2.3.3 is about, and docs/UI-STYLE.md calls a button without it a picture
   of a button. .01ms rather than 0 is a11y/apply.ts's value, chosen
   there because a zero-duration animation never fires animationend; the OS
   query and the in-app switch now behave identically instead of one of them
   dropping the cue. */
@media (prefers-reduced-motion:reduce){
  .rga-opt,.rga-plate,.rga-switch__track,.rga-switch__knob{transition-duration:.01ms}
}
`;
